import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js"; // Reused component
import Features from "../../../../Components/Features/ThreeColumnPages"; // Reused component
import {
  FaSearch,
  FaUsers,
  FaChartLine,
  FaHandshake,
  FaAward,
  FaMapMarkerAlt,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

// UI/UX User Testing in DIFC Page Component
export const UIUXUserTestingInDIFC = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="UI/UX User Testing in DIFC" />

      {/* Hero Section */}
      <MainFeature1
        subheading={<Subheading>Unlocking Usability Insights</Subheading>}
        heading={
          <Heading>
            Optimize Your Digital Products with{" "}
            <span tw="text-primary-500">UI/UX User Testing in DIFC</span>
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, we specialize in delivering data-driven user
            testing services tailored to businesses in **DIFC**, Dubai. Our
            expertise ensures your digital products are intuitive, accessible,
            and optimized for maximum user satisfaction.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1515378960530-7c0da6231fb1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // User testing session
          "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // UI/UX analysis
          "https://images.unsplash.com/photo-1605379399642-870262d3d051?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // Data analytics visualization
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Get Started Today"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Us?</Subheading>}
        heading="Your Trusted Partner for UI/UX User Testing in DIFC"
        description="We combine expertise, creativity, and innovation to deliver unmatched value."
        cards={[
          {
            icon: <FaSearch color="#ff5464" size={40} />,
            title: "Identify Usability Issues",
            description:
              "Discover hidden pain points that affect user satisfaction.",
          },
          {
            icon: <FaUsers color="#ff5464" size={40} />,
            title: "Enhanced Accessibility",
            description:
              "Ensure your product is inclusive and accessible to all users.",
          },
          {
            icon: <FaMapMarkerAlt color="#ff5464" size={40} />,
            title: "Localized Expertise",
            description:
              "Tailored solutions for businesses operating in DIFC and Dubai.",
          },
          {
            icon: <FaChartLine color="#ff5464" size={40} />,
            title: "Data-Driven Decisions",
            description:
              "Base your design improvements on real user feedback and analytics.",
          },
          {
            icon: <FaHandshake color="#ff5464" size={40} />,
            title: "Client-Centric Collaboration",
            description:
              "Work closely with you to ensure alignment with your vision and goals.",
          },
          {
            icon: <FaAward color="#ff5464" size={40} />,
            title: "Proven Track Record",
            description:
              "Delivering exceptional results for clients in DIFC since [Year].",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our Streamlined Process for UI/UX User Testing in DIFC"
        description={
          <Description>
            Our DIFC-focused process ensures quality, efficiency, and measurable
            results:
            <ul tw="mt-4 list-disc pl-6">
              <li>
                Discovery & Planning: Understand your business, audience, and
                goals.
              </li>
              <li>
                Test Design: Create test scenarios and tasks aligned with user
                behavior.
              </li>
              <li>User Recruitment: Identify and recruit participants.</li>
              <li>
                Conduct Testing: Perform usability tests using tools like
                heatmaps, session recordings, and surveys.
              </li>
              <li>
                Analysis & Reporting: Analyze findings and provide actionable
                recommendations.
              </li>
              <li>
                Implementation Support: Assist in implementing changes based on
                test results.
              </li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // User behavior analysis
          "https://images.unsplash.com/photo-1535957998253-26ae1ef29506?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // Testing devices setup
          "https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // Usability report analysis
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="Results That Speak for Themselves"
        description="Here’s how our UI/UX user testing services have empowered businesses in DIFC."
        cards={[
          {
            icon: <FaSearch color="#ff5464" size={40} />,
            title: "Improved App Navigation",
            description:
              "Helped a fintech startup reduce app drop-off rates by 35%.",
          },
          {
            icon: <FaHandshake color="#ff5464" size={40} />,
            title: "Increased Engagement",
            description: "Boosted e-commerce checkout completion rates by 50%.",
          },
          {
            icon: <FaChartLine color="#ff5464" size={40} />,
            title: "Higher Satisfaction Scores",
            description:
              "Achieved a 40% improvement in user satisfaction for a healthcare platform.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={
          <Subheading>Ready to Elevate Your User Experience?</Subheading>
        }
        heading="Let’s Unlock Your Product’s Full Potential in DIFC"
        description={
          <Description>
            Partner with the leading provider of UI/UX user testing services in
            DIFC to ensure your digital products deliver exceptional usability,
            accessibility, and user satisfaction. Whether you’re launching a new
            platform or enhancing an existing one, we’re here to help.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1520333789090-1afc82db536a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // Team analyzing results
          "https://images.unsplash.com/photo-1512428559087-560fa5ceab42?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // Dubai tech business district
          "https://images.unsplash.com/photo-1556155092-490a1ba16284?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80", // User experience improvement
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Today"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
